import {
  CustomStyleType,
  ResearchAdvancedTimeType,
  ResponseLengthType,
  UserSettingsType,
} from '@webclient/shared-types/auth';
import {
  SearchModeType,
  SearchScopeType,
} from '@webclient/shared-types/search-result/types';
import { VoiceType } from '@webclient/shared-types/settings';

import { createSlice, PayloadAction } from '@reduxjs/toolkit';

type StateType = {
  userGeoLocation: {
    lat: number;
    lng: number;
  };
  isModalOpen: boolean;
  userSpecificSettings: {
    [key: string]: UserSettingsType;
  };
};

const initialState: StateType = {
  userGeoLocation: {
    lat: 0,
    lng: 0,
  },
  isModalOpen: false,
  userSpecificSettings: {},
};

const appSettings = createSlice({
  name: 'appSettings',
  initialState,
  reducers: {
    setUserGeoLocation: (
      state,
      action: PayloadAction<StateType['userGeoLocation']>,
    ) => {
      state.userGeoLocation = {
        lat: action.payload.lat,
        lng: action.payload.lng,
      };
    },
    initializedUserSpecificSettings: (state) => {
      return state;
    },
    toggleModal: (state) => {
      state.isModalOpen = !state.isModalOpen;
    },
    setUploadedToKnowledgeBaseByUserId: (
      state,
      action: PayloadAction<{ userId: string; shouldSave: boolean }>,
    ) => {
      const { shouldSave, userId } = action.payload;

      state.userSpecificSettings[userId] = {
        ...state.userSpecificSettings[userId],
        shouldSaveUploadsToKnowledgeBase: shouldSave,
      };
    },
    setSearchScopeByUserId: (
      state,
      action: PayloadAction<{ userId: string; searchScope: SearchScopeType }>,
    ) => {
      const { searchScope, userId } = action.payload;

      state.userSpecificSettings[userId] = {
        ...state.userSpecificSettings[userId],
        searchScope,
      };
    },
    setCustomStyleByUserId: (
      state,
      action: PayloadAction<{ userId: string; customStyle: CustomStyleType }>,
    ) => {
      const { customStyle, userId } = action.payload;

      state.userSpecificSettings[userId] = {
        ...state.userSpecificSettings[userId],
        customStyle,
      };
    },
    setUseCustomStyleByUserId: (
      state,
      action: PayloadAction<{ userId: string; useCustomStyle: boolean }>,
    ) => {
      const { useCustomStyle, userId } = action.payload;

      state.userSpecificSettings[userId] = {
        ...state.userSpecificSettings[userId],
        useCustomStyle,
      };
    },
    setResponseLengthByUserId: (
      state,
      action: PayloadAction<{
        userId: string;
        responseLength: ResponseLengthType;
      }>,
    ) => {
      const { responseLength, userId } = action.payload;

      state.userSpecificSettings[userId] = {
        ...state.userSpecificSettings[userId],
        responseLength,
      };
    },
    setSearchModeByUserId: (
      state,
      action: PayloadAction<{ userId: string; searchMode: SearchModeType }>,
    ) => {
      const { searchMode, userId } = action.payload;

      state.userSpecificSettings[userId] = {
        ...state.userSpecificSettings[userId],
        searchMode,
      };
    },
    setResearchAdvancedTimeByUserId: (
      state,
      action: PayloadAction<{
        userId: string;
        researchAdvancedTime: ResearchAdvancedTimeType;
      }>,
    ) => {
      const { researchAdvancedTime, userId } = action.payload;

      state.userSpecificSettings[userId] = {
        ...state.userSpecificSettings[userId],
        researchAdvancedTime,
      };
    },
    setBotByUserId: (
      state,
      action: PayloadAction<{ userId: string; bot: UserSettingsType['bot'] }>,
    ) => {
      const { bot, userId } = action.payload;
      state.userSpecificSettings[userId] = {
        ...state.userSpecificSettings[userId],
        bot,
      };
    },
    setVoiceTypeByUserId: (
      state,
      action: PayloadAction<{ userId: string; type: VoiceType }>,
    ) => {
      const { type, userId } = action.payload;
      state.userSpecificSettings[userId] = {
        ...state.userSpecificSettings[userId],
        voice: {
          ...state.userSpecificSettings[userId].voice,
          type,
        },
      };
    },
    setVoiceSpeedByUserId: (
      state,
      action: PayloadAction<{ userId: string; speed: number }>,
    ) => {
      const { speed, userId } = action.payload;
      state.userSpecificSettings[userId] = {
        ...state.userSpecificSettings[userId],
        voice: {
          ...state.userSpecificSettings[userId].voice,
          speed,
        },
      };
    },
    setVoiceAutoplayByUserId: (
      state,
      action: PayloadAction<{ userId: string; autoplay: boolean }>,
    ) => {
      const { autoplay, userId } = action.payload;
      state.userSpecificSettings[userId] = {
        ...state.userSpecificSettings[userId],
        voice: {
          ...state.userSpecificSettings[userId].voice,
          autoplay,
        },
      };
    },

    setVoiceZonosTtsByUserId: (
      state,
      action: PayloadAction<{ userId: string; zonos: boolean }>,
    ) => {
      const { zonos, userId } = action.payload;

      state.userSpecificSettings[userId] = {
        ...state.userSpecificSettings[userId],
        voice: {
          ...state.userSpecificSettings[userId].voice,
          zonos,
        },
      };
    },
    setAdvancedPdfIngestionByUserId: (
      state,
      action: PayloadAction<{ userId: string; advancedPdfIngestion: boolean }>,
    ) => {
      const { userId, advancedPdfIngestion } = action.payload;
      state.userSpecificSettings[userId] = {
        ...state.userSpecificSettings[userId],
        advancedPdfIngestion,
      };
    },
  },
});

export const {
  setUserGeoLocation,
  toggleModal,
  setUploadedToKnowledgeBaseByUserId,
  setSearchScopeByUserId,
  initializedUserSpecificSettings,
  setCustomStyleByUserId,
  setUseCustomStyleByUserId,
  setResponseLengthByUserId,
  setSearchModeByUserId,
  setResearchAdvancedTimeByUserId,
  setBotByUserId,
  setVoiceTypeByUserId,
  setVoiceSpeedByUserId,
  setVoiceAutoplayByUserId,
  setVoiceZonosTtsByUserId,
  setAdvancedPdfIngestionByUserId,
} = appSettings.actions;

export default appSettings.reducer;
